import React, { useEffect, useState } from 'react';
import { isFollowing, followUser, unfollowUser } from '../services/userService';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

const FollowButton = ({ targetUid, variant = 'primary' }) => {
    const [following, setFollowing] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const mountedRef = { current: true };
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (!mountedRef.current) return;

            if (!firebaseUser) {
                setFollowing(false);
                return;
            }

            try {
                const res = await isFollowing(firebaseUser.uid, targetUid);
                if (mountedRef.current) setFollowing(res);
            } catch (error) {
                console.error('Failed to resolve follow state', error);
                if (mountedRef.current) setFollowing(false);
            }
        });

        return () => {
            mountedRef.current = false;
            unsubscribe();
        };
    }, [targetUid]);

    const toggle = async () => {
        if (!targetUid || targetUid === auth.currentUser?.uid) {
            return;
        }
        if (!auth.currentUser) {
            // Silently return if not signed in - no alert
            return;
        }
        
        // Optimistically update UI immediately
        const newFollowingState = !following;
        setFollowing(newFollowingState);
        setLoading(true);
        
        try {
            if (following) {
                await unfollowUser(auth.currentUser.uid, targetUid);
                // State already updated optimistically
            } else {
                await followUser(auth.currentUser.uid, targetUid);
                // State already updated optimistically
            }
        } catch (e) {
            console.error('Follow/unfollow error:', e);
            // Revert optimistic update on error
            setFollowing(!newFollowingState);
        } finally {
            setLoading(false);
        }
    };

    const isOwnTarget = Boolean(targetUid && auth.currentUser?.uid === targetUid);

    const isCompact = variant === 'compact';

    const className =
        isCompact
            ? [
                'px-4 py-2 rounded-full text-sm font-semibold transition w-full',
                following
                    ? 'bg-white/10 border border-white/15 text-white hover:bg-white/15'
                    : 'bg-[#ff2a2a] text-white hover:bg-[#ff1111]',
            ].join(' ')
            : [
                'w-full select-none rounded-full bg-gradient-to-br from-[#ff3b3b] to-[#ff0000]',
                'px-5 py-3 text-base font-bold text-white shadow-[0_6px_20px_rgba(255,0,0,0.35)] ring-1 ring-red-500/30',
                'active:scale-[0.98] transition',
            ].join(' ');

    const style = isCompact ? undefined : { textShadow: '0 0 12px rgba(255,255,255,0.25)' };

    if (!targetUid || isOwnTarget) {
        return null;
    }

    return (
        <button
            onClick={toggle}
            disabled={loading}
            className={className}
            style={style}
        >
            {loading ? 'Please wait...' : following ? 'Following' : 'Follow'}
        </button>
    );
};

export default FollowButton;
